import React, { SyntheticEvent, useCallback, useState } from "react";

import { Select, Toggle } from "@at/design-system-elements";
import { TypeFiesta, TypeInsignia } from "@at/design-system-foundation";
import { getChannelSingular } from "@at/formatter-utils";
import {
  dataAttrTestId,
  dataAttrGUI,
} from "@at/formatter-utils/lib/data-attr-utils/data-attr-utils";

import { ANNUAL_MILEAGE_SELECT_OPTIONS } from "./annual-mileage-values";

import * as Styled from "./charging.styles";
import { ChargingCalculator, EstimateBacking } from "./charging.styles";
import {
  calculateHoursPerWeekToCharge,
  getDefaultChargingSpeed,
  locationToggleOptions,
} from "./hours-per-week-to-charge.utils";
import { ChargerLocationType } from "../../../__generated__/graphql-types";
import { ChargingExplainedProps } from "../../../types/chargin-data.types";
import { useChannel } from "../../../utils/hooks/use-channel";
import { useTracking } from "../../../utils/tracking/use-tracking";

export const HoursPerWeekToCharge = ({ chargers }: ChargingExplainedProps) => {
  const { trackEvent } = useTracking();
  const channel = useChannel();
  const channelSingular = getChannelSingular(channel);
  chargers = chargers || [];
  const homeChargers = chargers.filter(
    (c) => ChargerLocationType.HOME === c?.chargerLocation,
  );
  const publicChargers = chargers.filter(
    (c) => ChargerLocationType.PUBLIC === c?.chargerLocation,
  );

  const setDefault = () => {
    if (homeChargers.length > 0) {
      return ChargerLocationType.HOME;
    }
    return ChargerLocationType.PUBLIC;
  };

  const [locationToggle, setLocationToggle] = useState(setDefault());
  const [mileage, setMileage] = useState(8000);

  const availableChargers =
    locationToggle === ChargerLocationType.PUBLIC
      ? publicChargers
      : homeChargers;
  const [selectedChargingSpeed, setSelectedChargingSpeed] = useState(
    getDefaultChargingSpeed(availableChargers),
  );

  const getMilesPerHourChargeTime = (chargingSpeed: string) => {
    return availableChargers.find(
      (charger) => charger?.description === chargingSpeed,
    )?.milesRangePerHourChargeTime;
  };
  const [milesRangePerHourChargeTime, setMilesRangePerHourChargeTime] =
    useState(
      selectedChargingSpeed
        ? getMilesPerHourChargeTime(selectedChargingSpeed)
        : 0,
    );
  const [hoursPerWeek, setHoursPerWeek] = useState(
    calculateHoursPerWeekToCharge(mileage, milesRangePerHourChargeTime),
  );
  const getChargerOptions = useCallback(() => {
    return availableChargers.map((charger) => ({
      value: charger?.description || 0,
      displayValue: charger?.description || "",
    }));
  }, [availableChargers]);

  if (chargers.length === 0) {
    return null;
  }

  const setDefaultHoursPerWeekCharge = (
    mileage: number,
    milesRangePerHourChargeTime: number | null | undefined,
  ) => {
    const hoursPerWeek = calculateHoursPerWeekToCharge(
      mileage,
      milesRangePerHourChargeTime,
    );
    if (hoursPerWeek) {
      setHoursPerWeek(hoursPerWeek);
    }
  };

  const handleMileageChange = (event: SyntheticEvent) => {
    const eventTarget = event.target as HTMLInputElement;
    const selectedMileage = Number.parseFloat(eventTarget.value);

    setMileage(selectedMileage);
    setDefaultHoursPerWeekCharge(selectedMileage, milesRangePerHourChargeTime);
    trackEvent({ trackingLabel: "mileage-dropdown-change" });
  };

  const handleChargerChange = (event: SyntheticEvent) => {
    const selectedMilesRangePerHourChargeTime =
      availableChargers.find(
        (charger) =>
          charger?.description === (event.target as HTMLInputElement).value,
      )?.milesRangePerHourChargeTime || 0;
    setSelectedChargingSpeed((event.target as HTMLInputElement).value);
    setMilesRangePerHourChargeTime(selectedMilesRangePerHourChargeTime);
    setDefaultHoursPerWeekCharge(mileage, selectedMilesRangePerHourChargeTime);
    trackEvent({ trackingLabel: "charger-dropdown-change" });
  };

  const handleLocationChange = (event: SyntheticEvent) => {
    const selectedLocation = (event.target as HTMLInputElement)
      .value as ChargerLocationType;
    const selectedChargerOptions =
      selectedLocation === ChargerLocationType.HOME
        ? homeChargers
        : publicChargers;
    const defaultCharge = getDefaultChargingSpeed(selectedChargerOptions);
    const milesRangePerHourChargeTime = selectedChargerOptions.find(
      (charger) => charger?.description === defaultCharge,
    )?.milesRangePerHourChargeTime;

    setLocationToggle(selectedLocation);
    setSelectedChargingSpeed(defaultCharge);
    setMilesRangePerHourChargeTime(milesRangePerHourChargeTime);
    setDefaultHoursPerWeekCharge(mileage, milesRangePerHourChargeTime);
    trackEvent({ trackingLabel: "charger-location-change" });
  };

  return (
    <>
      <Styled.ModalBodyStyled>
        <TypeInsignia as="h2" weight="400">
          How often will I need to charge?
        </TypeInsignia>

        <TypeFiesta as="p">
          You might be surprised to see how long a charge might last. Use this
          calculator to see how often you&apos;ll need to charge an electric{" "}
          {channelSingular}.
        </TypeFiesta>
        <ChargingCalculator>
          <TypeFiesta as="span" weight="500">
            Charging location
          </TypeFiesta>
          <Toggle
            id="charging-location"
            options={locationToggleOptions(
              locationToggle,
              publicChargers.length > 0,
              homeChargers.length > 0,
            )}
            name="credit-rating"
            onInteraction={handleLocationChange}
          />
          <Select
            id="annualMileageDropdown"
            name="Annual Mileage"
            label="Your annual mileage"
            options={ANNUAL_MILEAGE_SELECT_OPTIONS}
            attributes={{
              onChange: handleMileageChange,
              [dataAttrTestId]: "mileageDropdown",
              [dataAttrGUI]: "mileageDropdown",
              value: mileage,
            }}
          ></Select>
          <Select
            id="allChargersDropdown"
            name="Charging speed"
            label="Your charging speed"
            options={getChargerOptions()}
            attributes={{
              onChange: handleChargerChange,
              [dataAttrTestId]: "chargerSpeed",
              [dataAttrGUI]: "chargerSpeed",
              value: selectedChargingSpeed,
            }}
          ></Select>
        </ChargingCalculator>

        <EstimateBacking>
          <p>We estimate that you will need to charge for:</p>
          <TypeInsignia as="h3" weight="500" data-gui="hoursPerWeek">
            {hoursPerWeek ? (
              <>{`${hoursPerWeek} ${
                hoursPerWeek > 1 ? "hours" : "hour"
              } per week`}</>
            ) : null}
          </TypeInsignia>
          <p>
            This estimate is based on a new vehicle with optimum driving
            conditions.
          </p>

          <p>
            <TypeFiesta as="span" weight="500">
              Important to know:{" "}
            </TypeFiesta>{" "}
            There are some things that could affect how long your battery charge
            will last, including your driving style, the weather, and using your
            air conditioning and heating
          </p>
        </EstimateBacking>
      </Styled.ModalBodyStyled>
    </>
  );
};
